import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Anchor,
  ShieldCheck, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Phone, 
  Mail, 
  Layers,
  Sparkles,
  Ruler,
  Wrench,
  HelpCircle,
  Compass,
  CheckCircle2,
  AlertTriangle,
  Play
} from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import FAQSection from '../components/FAQSection';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Jordanker() {
  const heroImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/skruefundament-byggeplads-montering.jpg", { width: 1400, quality: 85 });
  const anchorImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 800, quality: 85 });
  const siteImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg", { width: 800, quality: 85 });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Jordanker til forankring - CM Skruefundament ApS",
    "description": "Jordanker til sikker forankring af gyngestativer, stolper, trampoliner og haveudstyr i hele Danmark.",
    "brand": {
      "@type": "Brand",
      "name": "CM Skruefundament"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "DKK",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "CM Skruefundament ApS",
        "url": "https://cmskruefundament.dk"
      }
    }
  };

  const useCases = [
    {
      title: 'Gyngestativer',
      desc: 'kræver et sikkert fæste ved benene',
      icon: Play,
      tag: 'Børnesikkerhed'
    },
    {
      title: 'Stolper',
      desc: 'skal fastholdes mod bevægelse og skæv belastning',
      icon: Ruler,
      tag: 'Hegn & overdækning'
    },
    {
      title: 'Trampoliner',
      desc: 'skal sikres efter stellets udformning',
      icon: ShieldCheck,
      tag: 'Stormsikring'
    },
    {
      title: 'Haveudstyr',
      desc: 'kan forankres mod vind og almindelig brug',
      icon: Anchor,
      tag: 'Udendørs forankring'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Vurdering',
      desc: 'af konstruktion, placering og jordbund',
      icon: Compass
    },
    {
      number: '02',
      title: 'Valg',
      desc: 'af jordanker, længde og passende fastgørelse',
      icon: Ruler
    },
    {
      number: '03',
      title: 'Montering',
      desc: 'med kontrol af retning, fæste og tilslutning',
      icon: Wrench
    },
    {
      number: '04',
      title: 'Eftersyn',
      desc: 'af anker og forbindelser efter installationen',
      icon: ShieldCheck
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Jordanker til sikker forankring i jorden | Få rådgivning nu</title>
        <meta 
          name="description" 
          content="Et jordanker giver et stabilt fæste til konstruktioner og udstyr, der skal blive stående sikkert udendørs. Få rådgivning og montering i hele Danmark." 
        />
        <link rel="canonical" href="https://cmskruefundament.dk/jordanker" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/jordanker" />
        <meta property="og:title" content="Jordanker til sikker forankring i jorden | Få rådgivning nu" />
        <meta 
          property="og:description" 
          content="Et jordanker giver et stabilt fæste til konstruktioner og udstyr, der skal blive stående sikkert udendørs. Få rådgivning og montering i hele Danmark." 
        />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/jordanker" />
        <meta name="twitter:title" content="Jordanker til sikker forankring i jorden | Få rådgivning nu" />
        <meta 
          name="twitter:description" 
          content="Et jordanker giver et stabilt fæste til konstruktioner og udstyr, der skal blive stående sikkert udendørs. Få rådgivning og montering i hele Danmark." 
        />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage} 
            alt="Professionelt jordanker og skruefundament monteret i jorden til stabil forankring" 
            className="w-full h-full object-cover object-center opacity-25 filter brightness-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 text-sm font-semibold mb-6 backdrop-blur-sm">
                <Anchor className="w-4 h-4 text-orange-400" />
                <span>Sikker & Stabil Forankring</span>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl min-[992px]:text-[2.75rem] font-black uppercase tracking-tight text-white mb-6 leading-tight">
                Jordanker til sikker forankring i jorden
              </h1>

              <p className="text-lg sm:text-xl text-slate-200 mb-8 leading-relaxed font-light">
                Et jordanker giver et stabilt fæste til konstruktioner og udstyr, der skal blive stående sikkert udendørs. Hos <Link to="/" className="font-bold text-white underline decoration-orange-400 hover:text-orange-300 transition-colors">CM Skruefundament</Link> hjælper vi dig med at vælge en løsning, der passer til både belastningen, fastgørelsen og jordbundsforholdene på det sted, hvor ankret skal monteres.
              </p>

              <div className="flex flex-wrap gap-4 items-center">
                <a
                  href="#kontakt-form"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded-xl shadow-lg hover:shadow-orange-500/25 transition-all flex items-center gap-2 transform active:scale-98"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="#anvendelse"
                  className="bg-slate-800/80 hover:bg-slate-700 text-white font-semibold px-6 py-3.5 rounded-xl border border-slate-700 transition-all backdrop-blur-sm"
                >
                  Se anvendelsesmuligheder
                </a>
              </div>

              {/* Quick trust metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-slate-800/80">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="w-5 h-5 text-orange-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-slate-300">Fast forankring</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Clock className="w-5 h-5 text-orange-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-slate-300">Hurtig montage</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Wrench className="w-5 h-5 text-orange-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-slate-300">Tilpasset beslag</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-5 h-5 text-orange-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-slate-300">Hele Danmark</span>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Jordanker"
                title="Få tilbud på jordankre"
                subtitle="Fortæl om opgaven og underlaget, så rådgiver vi om korrekt dimensionering og pris."
              />
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 1: Hvad er et jordanker, og hvornår er det den rigtige løsning? */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-orange-600 font-bold text-sm tracking-wide uppercase">
                <Sparkles className="w-4 h-4" />
                <span>Teknisk indsigt</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight">
                Hvad er et jordanker, og hvornår er det den rigtige løsning?
              </h2>

              <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
                <p>
                  Et jordanker er en komponent af stål, som føres eller skrues ned i jorden og forbindes med det element, der skal fastholdes. Det kan blandt andet være et gyngestativ, en stolpe eller en trampolin. Formen og fastgørelsen varierer, fordi forskellige projekter påvirkes forskelligt af træk og bevægelse.
                </p>
                <p>
                  Det rette valg afhænger af jordens fasthed, den forventede belastning og konstruktionens udformning. Et mindre haveprojekt stiller ikke de samme krav som en bærende konstruktion. Derfor bør længde, antal, placering og fastgørelse altid vurderes ud fra den konkrete opgave frem for en generel standardløsning.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-orange-200 transition-colors">
                  <div className="flex items-center gap-3 font-bold text-slate-900 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                      <Anchor className="w-4 h-4" />
                    </div>
                    <span>Forankring mod træk</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Sikrer stel og rammer mod vindstød, vip og uønsket bevægelse i terrænet.
                  </p>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-orange-200 transition-colors">
                  <div className="flex items-center gap-3 font-bold text-slate-900 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                      <Compass className="w-4 h-4" />
                    </div>
                    <span>Jordbundstilpasning</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Dimensioneres efter jordens type og fasthed for at opnå maksimalt hold.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-900 group">
                <img 
                  src={anchorImage} 
                  alt="Varmgalvaniseret stål jordanker til sikring mod træk og vindlast" 
                  className="w-full h-96 object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent flex flex-col justify-end p-6 sm:p-8">
                  <span className="text-orange-400 font-mono text-xs uppercase tracking-wider mb-1">Stærk forankring</span>
                  <p className="text-white text-lg font-bold">Stålkomponenter til udendørs sikring</p>
                  <p className="text-slate-300 text-sm mt-1">
                    Overfører træk- og vridningskræfter direkte til underliggende jordlag.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 2: Vælg jordanker efter konstruktion og jordbund */}
      <section id="anvendelse" className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-orange-600 font-bold text-sm tracking-wide uppercase mb-3">
              <Layers className="w-4 h-4" />
              <span>Anvendelsesområder</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-4">
              Vælg jordanker efter konstruktion og jordbund
            </h2>
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Jordankre bruges til mange udendørs projekter, men den samme type passer ikke nødvendigvis til dem alle. Blød jord, fast lerjord, sten og opfyld kan påvirke fæstet. Samtidig skal ankerets beslag passe til stolpen, rammen eller stellet, så belastningen overføres korrekt til jorden.
            </p>
          </div>

          {/* 4 Cards for list items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {useCases.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center font-bold">
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                        {item.tag}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      <strong>{item.title}</strong> {item.desc}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center text-xs font-semibold text-orange-600 gap-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span>Optimeret beslag & fæste</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dybdegående H3 sektioner */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* H3: Gyngestativ & stolper */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-5">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-orange-400 flex items-center justify-center font-bold">
                <Ruler className="w-5 h-5" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                Jordanker til gyngestativ og jordanker til stolper
              </h3>
              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Et jordanker til gyngestativ hjælper med at forhindre, at stativet vipper eller flytter sig under brug. Placeringen skal følge stativets ben og producentens anvisninger. Antallet kan variere, og fastgørelsen skal passe til materialet, uanset om benene er fremstillet af træ eller metal.
                </p>
                <p>
                  Et jordanker til stolper kan bruges ved eksempelvis hegn og lettere stolpekonstruktioner. Stolpens dimension, højde og påvirkning fra vind har betydning for valget. Ved større konstruktioner bør det vurderes, om opgaven kræver jordskruer eller et dimensioneret skruefundament frem for almindelig forankring.
                </p>
              </div>
              <div className="pt-2">
                <Link 
                  to="/jordskruer" 
                  className="inline-flex items-center gap-2 text-sm font-bold text-orange-600 hover:text-orange-700 group"
                >
                  <span>Overvejer du større konstruktioner? Læs om jordskruer</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* H3: Trampolin */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-5">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-orange-400 flex items-center justify-center font-bold">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                Jordanker trampolin, sådan holder du stellet bedre på plads
              </h3>
              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Jordanker trampolin dækker over løsninger, der fastholder trampolinens stel til jorden. Ankrene placeres omkring rammen og forbindes med egnede beslag eller stropper. Det er vigtigt, at fastgørelsen passer til den konkrete trampolin, og at producentens vejledning bliver fulgt ved både placering og tilspænding.
                </p>
                <p>
                  Et jordanker kan mindske risikoen for, at trampolinen flytter sig, men det erstatter ikke regelmæssig kontrol. Jord kan ændre sig efter kraftig regn, frost eller længere tids brug. Kontrollér derfor løbende, at anker, stropper og fastgørelsespunkter stadig sidder korrekt og ikke viser tegn på skader.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <span>Husk altid løbende eftersyn af stropper og jordens fasthed efter hårdt vejr eller frost.</span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Sektion 3: Jordanker montering, sådan skaber du et godt fæste */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-orange-600 font-bold text-sm tracking-wide uppercase mb-3">
              <Wrench className="w-4 h-4" />
              <span>Arbejdsproces</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-4">
              Jordanker montering, sådan skaber du et godt fæste
            </h2>
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Ved jordanker montering skal placeringen først afmærkes præcist. Området bør undersøges for kabler og rør, før arbejdet begynder. Ankret monteres i den foreskrevne retning og dybde, hvorefter forbindelsen til konstruktionen tilpasses og spændes, så belastningen fordeles jævnt.
            </p>
          </div>

          {/* Trin for trin grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div 
                  key={idx}
                  className="relative bg-slate-900 text-white rounded-2xl p-6 sm:p-7 shadow-lg flex flex-col justify-between overflow-hidden group hover:bg-slate-850 transition-colors"
                >
                  <div className="absolute top-3 right-4 text-5xl font-black text-slate-800 group-hover:text-slate-750 select-none transition-colors">
                    {step.number}
                  </div>
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center font-bold mb-6 shadow-md shadow-orange-500/30">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      <span className="text-orange-400">{step.title}</span>
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Afsluttende monteringsboks */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-3xl">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-orange-500" />
                <span>Korrekt montering sikrer det optimale resultat</span>
              </h3>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                En korrekt montering handler ikke kun om at få ankret langt nok ned. Hvis det står skævt, rammer større sten eller monteres i løs opfyld, kan fæstet blive utilstrækkeligt. Hos CM Skruefundament kan vi hjælpe med at vurdere, om du kan klare opgaven selv, eller om professionel montering er den bedste løsning.
              </p>
            </div>
            <a
              href="#kontakt"
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-lg text-sm shrink-0 transition-colors"
            >
              Kontakt for vurdering
            </a>
          </div>

        </div>
      </section>

      {/* Sektion 4: Forskellen på jordanker, jordskruer og skruefundament */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-orange-400 font-bold text-sm tracking-wide uppercase mb-3">
              <Compass className="w-4 h-4" />
              <span>Sammenligning & Rådgivning</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mb-4">
              Forskellen på jordanker, jordskruer og skruefundament
            </h2>
            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              <p>
                Et jordanker bruges primært til at fastholde en genstand mod træk, vip eller vandret bevægelse. En <Link to="/jordskruer" className="font-bold text-white underline decoration-orange-400 hover:text-orange-300 transition-colors">jordskrue</Link> er en stålskrue, der kan overføre belastninger til de bærende jordlag. Et <Link to="/skruefundering" className="font-bold text-white underline decoration-orange-400 hover:text-orange-300 transition-colors">skruefundament</Link> er den samlede funderingsløsning, hvor en eller flere jordskruer dimensioneres til konstruktionen.
              </p>
              <p>
                Begreberne kan overlappe, men navnet alene fortæller ikke, hvad produktet kan bære. Til <Link to="/terrasseskruer" className="font-bold text-white underline decoration-orange-400 hover:text-orange-300 transition-colors">terrasser</Link>, skure og andre bærende konstruktioner bør du ikke vælge et almindeligt jordanker uden en faglig vurdering. Her kan jordskruer og professionel skruefundering være mere relevante løsninger.
              </p>
            </div>
          </div>

          {/* 3-vejs sammenligningskort */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Kort 1: Jordanker */}
            <div className="bg-slate-800/90 rounded-2xl p-6 sm:p-8 border border-slate-700 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold mb-4">
                  <Anchor className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Jordanker</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Primært beregnet til at fastholde udstyr mod træk, vip og vandret bevægelse (f.eks. trampoliner, gyngestativer og haveudstyr).
                </p>
              </div>
              <div className="text-xs text-orange-400 font-semibold pt-4 border-t border-slate-700/60">
                Fokus: Fastholdelse & stormsikring
              </div>
            </div>

            {/* Kort 2: Jordskruer */}
            <div className="bg-slate-800/90 rounded-2xl p-6 sm:p-8 border border-slate-700 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold mb-4">
                  <Ruler className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Jordskruer</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Det fysiske produkt i varmgalvaniseret stål, som overfører konstruktionens faktiske vægt og laster ned til de faste jordlag.
                </p>
              </div>
              <div className="text-xs text-orange-400 font-semibold pt-4 border-t border-slate-700/60">
                <Link to="/jordskruer" className="hover:underline flex items-center gap-1">
                  Se mere om jordskruer <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Kort 3: Skruefundament */}
            <div className="bg-slate-800/90 rounded-2xl p-6 sm:p-8 border border-orange-500/40 bg-gradient-to-b from-slate-800 to-slate-850 flex flex-col justify-between shadow-xl">
              <div>
                <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center font-bold mb-4">
                  <Layers className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Skruefundament</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Den komplette, dimensionerede funderingsløsning for huse, terrasser, skure og tilbygninger med dokumenteret bæreevne.
                </p>
              </div>
              <div className="text-xs text-orange-400 font-semibold pt-4 border-t border-slate-700/60">
                <Link to="/skruefundering" className="hover:underline flex items-center gap-1">
                  Læs om skruefundering <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 5: Få hjælp til jordankre i hele Danmark */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-orange-600 font-bold text-sm tracking-wide uppercase">
                <MapPin className="w-4 h-4" />
                <span>Landsdækkende Service</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight">
                Få hjælp til jordankre i hele Danmark
              </h2>

              <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
                <p>
                  Hos CM Skruefundament hjælper vi private og professionelle med at finde den rette løsning til projektet. Vi rådgiver også om stålfundamenter som <Link to="/groundplug" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">GroundPlug</Link> og hele sortimentet i <Link to="/skruer" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">vores skruer</Link>. Læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">om os</Link>. Vi ser på anvendelsen, jordbunden og fastgørelsen, før vi anbefaler et produkt eller en monteringsmetode. Det giver dig et bedre grundlag for at vælge sikkert og undgå unødige fejlkøb.
                </p>
                <p>
                  Vi arbejder med projekter i Jylland, på Fyn og på Sjælland. Se referencer under <Link to="/projekter" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">vores projekter</Link>. Du kan altid <Link to="/kontakt" className="font-bold text-orange-600 underline hover:text-orange-700 transition-colors">kontakte os</Link>, hvis du vil købe jordankre, har spørgsmål til montering eller er i tvivl om forskellen på forankring og fundering. Sammen finder vi en løsning, der passer til konstruktionen og forholdene på din grund.
                </p>
              </div>

              {/* Kontakt hurtig links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <a
                  href="tel:+4521406560"
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-orange-500 hover:bg-orange-50/50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-orange-100 group-hover:bg-orange-500 text-orange-600 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Ring direkte til os</div>
                    <div className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors">+45 21 40 65 60</div>
                  </div>
                </a>

                <a
                  href="mailto:kontakt@cmskruefundament.dk"
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-orange-500 hover:bg-orange-50/50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-orange-100 group-hover:bg-orange-500 text-orange-600 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Send os en e-mail</div>
                    <div className="font-bold text-slate-900 group-hover:text-orange-600 text-sm transition-colors">kontakt@cmskruefundament.dk</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200">
                <img 
                  src={siteImage} 
                  alt="CM Skruefundament udfører præcis forankring og montage af jordankre i hele Danmark" 
                  className="w-full h-80 object-cover object-center"
                />
                <div className="p-6 bg-slate-900 text-white">
                  <h3 className="font-bold text-lg mb-2">Professionel rådgivning</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Vi rådgiver om valg af ankre, beslag og montering, så dine udendørs konstruktioner står sikkert året rundt.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 5.5: FAQ */}
      <FAQSection
        badgeText="Ofte stillede spørgsmål"
        title="Spørgsmål & svar om jordankre"
        description="Få svar på de vigtigste spørgsmål om trækbelastning, forankring, anvendelsesmuligheder og montering af jordankre."
        themeColor="orange"
        bgClass="bg-white"
        items={[
          {
            question: "Hvordan virker et jordanker, og hvad er forskellen på et jordanker og en jordskrue?",
            answer: `Et jordanker er specialudviklet til at modstå kraftige træk- og sideværtskræfter (trækbelastning) ud over ren vertikal trykstyrke, hvilket gør det ideelt til barduner, master, skråningssikring og læhegn. Hvor en standard jordskrue primært optager lodret tryk fra en bygningskonstruktion, udnytter jordankret sine udvidede skiveblade eller ekspanderende forankringsmekanismer til at bide sig fast i den komprimerede jordmasse.

Når ankret belastes i trækretningen, overføres kraften til en kegleformet jordmasse over ankret, hvilket giver en formidabel fastholdelsesevne mod opadgående og skrå træk.`
          },
          {
            question: "Hvilke typer projekter og konstruktioner anvender typisk jordankre?",
            answer: `Jordankre anvendes bredt inden for både privat havebyggeri, landbrug, erhverv og infrastruktur, hvor der opstår store vindkræfter eller horisontale trækpåvirkninger. Typiske anvendelser tæller forankring af høje hegn, støttemure, barduner til flagstænger og master, store telte og pavilloner, solcelleanlæg i åbent terræn samt sikring af skråninger mod jordskred.

De er også særdeles velegnede til stormsikring af trampoliner, legepladsudstyr og mindre havebygninger i kystnære områder.`
          },
          {
            question: "Hvor stor en trækbelastning kan et professionelt jordanker modstå?",
            answer: `Trækkapaciteten for et jordanker afhænger direkte af ankrets type, bladets diameter, installationsdybden samt jordens geotekniske tæthed og fugtighedsniveau. Mindre håndmonterede jordankre kan typisk optage trækbelastninger fra 500 kg op til 1,5 tons, mens kraftige mekanisk installerede spiralankre og forankringsspyd kan modstå træk på mellem 3 og over 10 tons pr. punkt.

Vi hjælper altid med at beregne og dimensionere det korrekte antal ankre ud fra konstruktionens forventede vind- og trækpåvirkninger.`
          },
          {
            question: "Kræver installation af jordankre specialmaskiner, eller kan man gøre det selv?",
            answer: `Lette jordankre til eksempelvis havehegn, barduner og mindre læskærme kan ofte installeres som et gør-det-selv projekt ved hjælp af en manuel slagdorn, borehammer eller et simpelt indskruningshåndtag. Til dybere forankringer, skråningssikring og større erhvervsprojekter, hvor der stilles specifikke krav til trækdokumentation og dybde, anvendes specialiserede hydrauliske slagdrivere eller momentmotorer.

Vi tilbyder både levering af komplette ankersæt til selvmontage og professionel totalløsning med maskinmontage og træktest.`
          },
          {
            question: "Hvordan sikres jordankret mod korrosion og rustangreb under jorden?",
            answer: `Alle vores professionelle jordankre er fremstillet af stærkt stål og varmgalvaniseret i et tykt zinklag efter de strengeste europæiske kvalitetsnormer (EN ISO 1461). Den metallurgiske binding mellem zinken og stålet danner en uigennemtrængelig barriere, der beskytter metallet mod fugt, ilt, syrer og salte i jorden.

Dette sikrer en vedligeholdelsesfri levetid på mange årtier, selv i aggressive jordbundsforhold eller ved placering tæt på havluft og grundvand.`
          }
        ]}
      />

      {/* Sektion 6: Kontaktformular */}
      <section id="kontakt" className="py-16 lg:py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-sm font-semibold mb-3">
              <Sparkles className="w-4 h-4" />
              <span>Uforpligtende tilbud</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900">
              Få et uforpligtende tilbud på jordanker
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto text-base">
              Fortæl os om dit projekt og dine ønsker, så rådgiver vi om det rette jordanker og udarbejder et uforpligtende tilbud.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-10">
            <ContactForm defaultService="Jordanker" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
